/** Synthesised Blue Max effects (C), owned and disposed by one game component. */
function defaultFactory() {
    if (typeof window === 'undefined') return null;
    const Audio = window.AudioContext || window.webkitAudioContext;
    return Audio ? new Audio() : null;
}

const EFFECTS = {
    shoot: [420, 170], bomb: [320, 90], impact: [110, 50], damage: [180, 65],
    takeoff: [220, 440, 660], airfield: [660, 440, 660], landed: [330, 440],
    lowFuel: [880, 440, 880], missionComplete: [440, 550, 660, 880], gameOver: [330, 220, 110],
};

export function createBlueMaxSound({ audioContextFactory = defaultFactory, muted = false } = {}) {
    let ctx = null;
    let disposed = false;
    let isMuted = !!muted;
    const active = new Set();

    function silence() {
        active.forEach(({ oscillator, gain }) => {
            try { oscillator.stop(); } catch (error) { /* Already stopped. */ }
            oscillator.disconnect();
            gain.disconnect();
        });
        active.clear();
    }

    return {
        unlock() {
            if (disposed) return;
            try {
                if (!ctx) ctx = audioContextFactory();
                if (ctx?.state === 'suspended') ctx.resume()?.catch(() => {});
            } catch (error) { /* Audio is optional when unavailable or disallowed. */ }
        },
        play(event) {
            const notes = EFFECTS[event?.type];
            if (!ctx || disposed || isMuted || !notes || active.size > 24) return;
            const short = ['shoot', 'bomb', 'impact', 'damage'].includes(event.type);
            const duration = short ? 0.055 : 0.12;
            notes.forEach((frequency, i) => {
                const oscillator = ctx.createOscillator();
                const gain = ctx.createGain();
                const entry = { oscillator, gain };
                active.add(entry);
                const at = ctx.currentTime + i * duration;
                oscillator.type = short ? 'sawtooth' : 'triangle';
                oscillator.frequency.setValueAtTime(frequency, at);
                gain.gain.setValueAtTime(short ? 0.035 : 0.07, at);
                gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
                oscillator.connect(gain);
                gain.connect(ctx.destination);
                oscillator.onended = () => {
                    oscillator.disconnect();
                    gain.disconnect();
                    active.delete(entry);
                };
                oscillator.start(at);
                oscillator.stop(at + duration);
            });
        },
        setMuted(value) {
            isMuted = !!value;
            if (isMuted) silence();
        },
        silence,
        dispose() {
            if (disposed) return;
            disposed = true;
            silence();
            if (ctx) ctx.close()?.catch(() => {});
        },
    };
}
